import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Collapse from "@material-ui/core/Collapse";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import InputLabel from "@material-ui/core/InputLabel";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Title from "../../components/Title";
import Typography from "@material-ui/core/Typography";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ClientDetailPanel from "./ClientDetailPanel";
import {
  OCORRENCIAS_FILTERS,
  ProductIcon,
  SITUACAO_FILTERS,
  VENC_SITUACAO_FILTERS,
  ocorrenciasParam,
  buildMonthOptions,
  formatDateBr,
  formatDocumento,
  formatMonthLabel,
  formatPhoneDisplay,
  seloColor,
  seloLabelKey,
  seloTooltipKey,
  situacaoColor,
  situacaoLabel,
} from "./helpers";

const COLUMNS = 7;
const VENC_COLUMNS = 6;
// escapeValue off: o React já escapa; sem isso "Out/2026" vira "Out&#x2F;2026".
const tv = (key, options) =>
  i18n.t(`carteiraVencimentos.${key}`, { interpolation: { escapeValue: false }, ...options });

const to = (key) => i18n.t(`carteiraOcorrencias.${key}`);

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  search: {
    minWidth: 320,
  },
  filter: {
    minWidth: 180,
  },
  count: {
    marginLeft: "auto",
    color: theme.palette.text.secondary,
  },
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  chip: {
    color: "#FFF",
    fontWeight: "bold",
  },
  row: {
    cursor: "pointer",
  },
  produtos: {
    display: "flex",
    gap: 4,
  },
  detailCell: {
    padding: 0,
    borderBottom: 0,
  },
  aviso: {
    padding: theme.spacing(1, 2),
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  resumo: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
  },
  clienteDoc: {
    display: "block",
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  situacaoCell: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
  },
}));

const CarteiraClientes = () => {
  const classes = useStyles();

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [situacaoFilter, setSituacaoFilter] = useState("");
  const [insurerFilter, setInsurerFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [ocorrenciasFilter, setOcorrenciasFilter] = useState("");
  const [insurerOptions, setInsurerOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [aviso, setAviso] = useState(null);
  const [expanded, setExpanded] = useState(null);

  // Visão "Mês de vencimento" (uma linha por apólice). Estado separado: os
  // filtros da lista por cliente ficam intactos enquanto houver mês.
  const [mes, setMes] = useState("");
  const [vSituacao, setVSituacao] = useState("");
  const [vRows, setVRows] = useState([]);
  const [vCount, setVCount] = useState(0);
  const [vHasMore, setVHasMore] = useState(false);
  const [vLoading, setVLoading] = useState(false);
  const [vError, setVError] = useState(false);
  const [vResumo, setVResumo] = useState(null);
  const [vSemDocumento, setVSemDocumento] = useState(0);
  const [vExpanded, setVExpanded] = useState(null);
  const monthOptions = useMemo(() => buildMonthOptions(new Date()), []);

  const fetchPortfolio = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/carteira", {
        params: {
          pageNumber,
          searchParam: searchParam || undefined,
          situacao: situacaoFilter || undefined,
          insurer: insurerFilter || undefined,
          product: productFilter || undefined,
          ocorrencias: ocorrenciasParam(ocorrenciasFilter),
        },
      });
      setRows((previous) => (pageNumber === 1 ? data.rows : [...previous, ...data.rows]));
      setCount(data.count);
      setHasMore(data.hasMore);
      setInsurerOptions(data.insurerOptions || []);
      setProductOptions(data.productOptions || []);
      setAviso(data.avisoApolicesSemDocumento || null);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [pageNumber, searchParam, situacaoFilter, insurerFilter, productFilter, ocorrenciasFilter, mes]);

  useEffect(() => {
    if (mes) return undefined;
    const timeout = setTimeout(() => {
      fetchPortfolio();
    }, 300);
    return () => clearTimeout(timeout);
  }, [fetchPortfolio, mes]);

  useEffect(() => {
    if (!mes) return undefined;
    // `active` descarta a resposta de uma requisição antiga (troca de mês,
    // busca ou página enquanto a anterior ainda estava no ar).
    let active = true;
    const timeout = setTimeout(async () => {
      setVLoading(true);
      setVError(false);
      try {
        const { data } = await api.get("/carteira/vencimentos", {
          params: {
            mes,
            searchParam: searchParam || undefined,
            situacao: vSituacao || undefined,
            insurer: insurerFilter || undefined,
            product: productFilter || undefined,
            ocorrencias: ocorrenciasParam(ocorrenciasFilter),
            pageNumber,
          },
        });
        if (!active) return;
        setVRows((previous) => (pageNumber === 1 ? data.rows : [...previous, ...data.rows]));
        setVCount(data.count);
        setVHasMore(data.hasMore);
        setVResumo(data.resumo || null);
        setVSemDocumento(data.semDocumento || 0);
        setVLoading(false);
      } catch (err) {
        if (!active) return;
        // Mensagem fixa: nunca repassa texto da resposta ao usuário.
        if (pageNumber === 1) setVRows([]);
        setVError(true);
        setVLoading(false);
        toast.error(tv("erro"), { toastId: "carteira-vencimentos-erro" });
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [mes, searchParam, vSituacao, insurerFilter, productFilter, ocorrenciasFilter, pageNumber]);

  const resetAndSet = (setter) => (value) => {
    setPageNumber(1);
    setExpanded(null);
    setter(value);
  };

  const handleSearchChange = resetAndSet(setSearchParam);
  const handleSituacaoChange = resetAndSet(setSituacaoFilter);
  const handleVSituacaoChange = resetAndSet(setVSituacao);
  const handleInsurerChange = resetAndSet(setInsurerFilter);
  const handleProductChange = resetAndSet(setProductFilter);
  const handleOcorrenciasChange = resetAndSet(setOcorrenciasFilter);

  const handleMesChange = (value) => {
    setPageNumber(1);
    setExpanded(null);
    setVExpanded(null);
    setVRows([]);
    setVCount(0);
    setVHasMore(false);
    setVResumo(null);
    setVSemDocumento(0);
    setVError(false);
    setMes(value);
  };

  const toggleVExpanded = (policyId) => {
    setVExpanded((previous) => (previous === policyId ? null : policyId));
  };

  const toggleExpanded = (documento) => {
    setExpanded((previous) => (previous === documento ? null : documento));
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Carteira de Clientes</Title>
      </MainHeader>

      <Paper className={classes.toolbar} variant="outlined">
        <TextField
          className={classes.search}
          size="small"
          variant="outlined"
          placeholder="Buscar por nome ou CPF/CNPJ"
          value={searchParam}
          onChange={(event) => handleSearchChange(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl variant="outlined" size="small" className={classes.filter}>
          <InputLabel>Situação</InputLabel>
          {mes ? (
            <Select
              label="Situação"
              value={vSituacao}
              onChange={(event) => handleVSituacaoChange(event.target.value)}
            >
              {VENC_SITUACAO_FILTERS.map((option) => (
                <MenuItem key={option.value || "todas"} value={option.value}>
                  {tv(option.labelKey)}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <Select
              label="Situação"
              value={situacaoFilter}
              onChange={(event) => handleSituacaoChange(event.target.value)}
            >
              {SITUACAO_FILTERS.map((option) => (
                <MenuItem key={option.value || "todas"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filter}>
          <InputLabel>Seguradora</InputLabel>
          <Select
            label="Seguradora"
            value={insurerFilter}
            onChange={(event) => handleInsurerChange(event.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            {insurerOptions.map((option) => (
              <MenuItem key={option.chave} value={option.chave}>
                {option.label} ({option.quantidade})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filter}>
          <InputLabel>Produto</InputLabel>
          <Select
            label="Produto"
            value={productFilter}
            onChange={(event) => handleProductChange(event.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {productOptions.map((option) => (
              <MenuItem key={option.valor} value={option.valor}>
                {option.valor} ({option.quantidade})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filter}>
          <InputLabel id="carteira-ocorrencias-label">{to("label")}</InputLabel>
          <Select
            labelId="carteira-ocorrencias-label"
            label={to("label")}
            value={ocorrenciasFilter}
            onChange={(event) => handleOcorrenciasChange(event.target.value)}
          >
            {OCORRENCIAS_FILTERS.map((option) => (
              <MenuItem key={option.value || "todas"} value={option.value}>
                {to(option.labelKey)}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{to("helperVigente")}</FormHelperText>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filter}>
          <InputLabel id="carteira-mes-label">{tv("mes")}</InputLabel>
          <Select
            labelId="carteira-mes-label"
            label={tv("mes")}
            value={mes}
            onChange={(event) => handleMesChange(event.target.value)}
            MenuProps={{ style: { maxHeight: 360 } }}
          >
            <MenuItem value="">{tv("todos")}</MenuItem>
            {monthOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {formatMonthLabel(option, i18n.t.bind(i18n))}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body2" className={classes.count}>
          {mes
            ? vResumo
              ? tv("contador", { renovar: vResumo.totalARenovar, novas: vResumo.totalNovas })
              : tv("apolices", { count: vCount })
            : `${count} cliente(s)`}
        </Typography>
      </Paper>

      {mes ? (
        <Paper className={classes.tableWrapper} variant="outlined">
          {vResumo ? (
            <div className={classes.resumo}>
              <Chip size="small" label={`${tv("renovada")}: ${vResumo.renovada}`} style={{ backgroundColor: seloColor("Renovada"), color: "#FFF" }} />
              <Chip size="small" label={`${tv("vencida")}: ${vResumo.vencida}`} style={{ backgroundColor: seloColor("Vencida"), color: "#FFF" }} />
              <Chip size="small" label={`${tv("naoRenovada")}: ${vResumo.naoRenovada}`} style={{ backgroundColor: seloColor("Não renovada"), color: "#FFF" }} />
              <Chip
                size="small"
                label={
                  vResumo.novaPossivelRenovacao > 0
                    ? `${tv("nova")}: ${vResumo.nova} (${vResumo.novaPossivelRenovacao} ⚠)`
                    : `${tv("nova")}: ${vResumo.nova}`
                }
                style={{ backgroundColor: seloColor("Nova"), color: "#FFF" }}
              />
              <Chip size="small" variant="outlined" label={`${tv("possivelRenovacao")}: ${vResumo.possivelRenovacao}`} />
              {vResumo.semSeloOutros > 0 ? (
                <Chip size="small" variant="outlined" label={`${tv("semSelo")}: ${vResumo.semSeloOutros}`} />
              ) : null}
            </div>
          ) : null}
          {vSemDocumento > 0 ? (
            <div className={classes.aviso}>{tv("semDocumento", { count: vSemDocumento })}</div>
          ) : null}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tv("colCliente")}</TableCell>
                <TableCell>{tv("colProduto")}</TableCell>
                <TableCell>{tv("colSeguradora")}</TableCell>
                <TableCell>{tv("colVencimento")}</TableCell>
                <TableCell>{tv("colSituacao")}</TableCell>
                <TableCell>{tv("colCelular")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vRows.map((row) => (
                <React.Fragment key={row.policyId}>
                  <TableRow
                    hover
                    className={classes.row}
                    onClick={() => toggleVExpanded(row.policyId)}
                  >
                    <TableCell>
                      {row.nome}
                      <span className={classes.clienteDoc}>{formatDocumento(row.documento)}</span>
                    </TableCell>
                    <TableCell>{row.produto || "—"}</TableCell>
                    <TableCell>{row.seguradora || "—"}</TableCell>
                    <TableCell>
                      {formatDateBr(row.displayDate || row.endDate)}
                      {row.displayDateKind === "inicio" ? (
                        <span className={classes.clienteDoc}>{tv("legendaInicio")}</span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <div className={classes.situacaoCell}>
                        {row.selo && seloColor(row.selo) ? (
                          <Tooltip title={tv(seloTooltipKey(row.selo))}>
                            <Chip
                              size="small"
                              className={classes.chip}
                              style={{ backgroundColor: seloColor(row.selo) }}
                              label={tv(seloLabelKey(row.selo))}
                            />
                          </Tooltip>
                        ) : null}
                        {row.origem === "endosso" ? (
                          <Chip size="small" variant="outlined" label={tv("endosso")} />
                        ) : null}
                        {row.possivelRenovacao === true ? (
                          <Tooltip title={tv(row.selo === "Nova" ? "tooltipPossivelRenovacaoNova" : "tooltipPossivelRenovacao")}>
                            <span tabIndex={0} aria-label={tv(row.selo === "Nova" ? "tooltipPossivelRenovacaoNova" : "tooltipPossivelRenovacao")}>
                              <ReportProblemOutlinedIcon fontSize="small" style={{ color: "#B26A00", verticalAlign: "middle" }} />
                            </span>
                          </Tooltip>
                        ) : null}
                        {row.seloIncerto === true ? (
                          <Tooltip title={tv("tooltipSeloIncerto")}>
                            <span tabIndex={0} aria-label={tv("tooltipSeloIncerto")}>
                              <InfoOutlinedIcon fontSize="small" color="disabled" style={{ verticalAlign: "middle" }} />
                            </span>
                          </Tooltip>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>{formatPhoneDisplay(row.celular)}</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell className={classes.detailCell} colSpan={VENC_COLUMNS}>
                      <Collapse in={vExpanded === row.policyId} unmountOnExit>
                        {vExpanded === row.policyId ? (
                          <ClientDetailPanel documento={row.documento} />
                        ) : null}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}

              {vLoading ? <TableRowSkeleton columns={VENC_COLUMNS} /> : null}

              {!vLoading && vError && vRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={VENC_COLUMNS} align="center" style={{ padding: 32 }}>
                    <Typography variant="subtitle1">{tv("erro")}</Typography>
                  </TableCell>
                </TableRow>
              ) : null}

              {!vLoading && !vError && vRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={VENC_COLUMNS} align="center" style={{ padding: 32 }}>
                    <Typography variant="subtitle1">
                      {tv("vazio", { mes: formatMonthLabel(mes, i18n.t.bind(i18n)) })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>

          {vHasMore && !vLoading ? (
            <div style={{ textAlign: "center", padding: 8 }}>
              <Button onClick={() => setPageNumber((previous) => previous + 1)}>
                Carregar mais
              </Button>
            </div>
          ) : null}
        </Paper>
      ) : (
      <Paper className={classes.tableWrapper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Cliente</TableCell>
              <TableCell>CPF/CNPJ</TableCell>
              <TableCell>Celular</TableCell>
              <TableCell>Produtos</TableCell>
              <TableCell align="right">Apólices</TableCell>
              <TableCell>Próximo venc.</TableCell>
              <TableCell>Situação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <React.Fragment key={row.documento}>
                <TableRow
                  hover
                  className={classes.row}
                  onClick={() => toggleExpanded(row.documento)}
                >
                  <TableCell padding="checkbox">
                    <IconButton size="small">
                      {expanded === row.documento ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{row.nome}</TableCell>
                  <TableCell>{formatDocumento(row.documento)}</TableCell>
                  <TableCell>{formatPhoneDisplay(row.telefone)}</TableCell>
                  <TableCell>
                    <div className={classes.produtos}>
                      {row.produtos.map((product) => (
                        <ProductIcon
                          key={product}
                          product={product}
                          fontSize="small"
                          color="action"
                          titleAccess={product}
                        />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell align="right">{row.apolicesVigentes}</TableCell>
                  <TableCell>
                    {row.proximoVencimento
                      ? row.proximoVencimento.slice(8, 10) +
                        "/" +
                        row.proximoVencimento.slice(5, 7) +
                        "/" +
                        row.proximoVencimento.slice(0, 4)
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      className={classes.chip}
                      style={{ backgroundColor: situacaoColor(row.situacao) }}
                      label={situacaoLabel(row.situacao)}
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell className={classes.detailCell} colSpan={COLUMNS + 1}>
                    <Collapse in={expanded === row.documento} unmountOnExit>
                      {expanded === row.documento ? (
                        <ClientDetailPanel documento={row.documento} />
                      ) : null}
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}

            {loading ? <TableRowSkeleton columns={COLUMNS + 1} /> : null}

            {!loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS + 1} align="center" style={{ padding: 32 }}>
                  <Typography variant="subtitle1">Nenhum cliente encontrado.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>

        {hasMore && !loading ? (
          <div style={{ textAlign: "center", padding: 8 }}>
            <Button onClick={() => setPageNumber((previous) => previous + 1)}>
              Carregar mais
            </Button>
          </div>
        ) : null}

        {aviso && aviso.quantidade > 0 ? (
          <div className={classes.aviso}>{aviso.mensagem}</div>
        ) : null}
      </Paper>
      )}
    </MainContainer>
  );
};

export default CarteiraClientes;
